import * as Dagre from 'dagre';
import type { ModelGraph, GraphNode, GraphValue, AttributeValue } from '@wetron/common/ir';
import { opInputLabels } from './op-inputs.ts';

type NodeData = {
  opType: string;
  name: string;
  inputs: readonly string[];
  outputs: readonly string[];
  attributes: Readonly<Record<string, AttributeValue>>;
};

export type GraphOperationNodeData = NodeData & {
  graphNode: GraphNode;
  weightInputs: readonly {
    slot: number;
    label: string;
    name: string;
    shape: readonly number[];
    dtype: string;
  }[];
};

export type IoNodeData = NodeData & {
  opType: 'Input' | 'Output';
  graphValue: GraphValue;
  shape: readonly number[] | null;
  dtype: string | null;
};

export type GraphNodeData = GraphOperationNodeData | IoNodeData;

type FlowNodeBase = {
  id: string;
  position: { x: number; y: number };
  initialWidth: number;
  initialHeight: number;
};

export type GraphFlowNode = FlowNodeBase & {
  type: 'graphNode';
  data: GraphOperationNodeData;
};

export type IoFlowNode = FlowNodeBase & {
  type: 'ioNode';
  data: IoNodeData;
};

export type FlowNode = GraphFlowNode | IoFlowNode;

export type FlowEdge = {
  id: string;
  source: string;
  target: string;
  type: 'modelEdge';
  data: {
    readonly tensorName: string;
    readonly sourceOpType: string;
    readonly sourceNodeName: string;
    readonly targetOpType: string;
    readonly targetNodeName: string;
    readonly points?: readonly { x: number; y: number }[];
  };
};

const NODE_W = 220;
// Heights derived from node-card CSS (border:2 + padding:14 + header:16 for 13px/1.2×).
// SUBTITLE_H: margin-top(2) + line(13 for 11px/1.2×). META_MARGIN: .meta margin-top.
// ROW_H: weight-row margin(3) + padding(4) + line(12 for 10px/1.2×).
// ION_H: ioNode = base + meta-section (margin(5) + single text line(12)).
const CARD_BASE = 32;
const SUBTITLE_H = 15;
const META_MARGIN = 5;
const ROW_H = 19;
const ION_H = CARD_BASE + META_MARGIN + 12; // 49

// Max weight rows rendered on a node card. Beyond this we render a single
// "+N more" footer row instead - the full list is reachable via the side panel.
// Keeps StatefulPartitionedCall and similar high-arity nodes from blowing up
// vertical space and starving dagre's rank assignment.
export const WEIGHT_ROW_LIMIT = 8;

export type LayoutDirection = 'TB' | 'LR';

function graphNodeId(node: GraphNode, index: number): string {
  return `node::${index}::${node.name || node.opType}`;
}

export function filterGraph(graph: ModelGraph, query: string): ReadonlySet<string> {
  const q = query.trim().toLowerCase();
  if (!q) return new Set();

  const matches = new Set<string>();
  for (let index = 0; index < graph.nodes.length; index++) {
    const node = graph.nodes[index];
    if (graph.initializers.has(node.name)) continue;
    if (node.opType.toLowerCase().includes(q) || node.name.toLowerCase().includes(q)) {
      matches.add(graphNodeId(node, index));
    }
  }
  return matches;
}

export function modelGraphToFlow(
  graph: ModelGraph,
  options?: { rankdir?: LayoutDirection },
): { nodes: FlowNode[]; edges: FlowEdge[] } {
  const rankdir = options?.rankdir ?? 'TB';
  const g = new Dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir, nodesep: 60, ranksep: 100 });

  const flowNodes: FlowNode[] = [];
  const flowEdges: FlowEdge[] = [];
  const outputToNodeId = new Map<string, string>();
  const nodeIdToOpType = new Map<string, string>();
  const nodeIdToName = new Map<string, string>();

  // Disambiguate IO nodes that share a name. The name is normally unique, but
  // some malformed graphs duplicate it; appending the index keeps FlowNode.id
  // unique without breaking the common case.
  const usedIoIds = new Set<string>();
  const uniqueIoId = (prefix: string, name: string, i: number): string => {
    const base = `${prefix}::${name}`;
    if (!usedIoIds.has(base)) {
      usedIoIds.add(base);
      return base;
    }
    return `${base}#${i}`;
  };

  for (let i = 0; i < graph.inputs.length; i++) {
    const gv = graph.inputs[i];
    const id = uniqueIoId('input', gv.name, i);
    flowNodes.push({
      id,
      type: 'ioNode',
      position: { x: 0, y: 0 },
      data: {
        opType: 'Input',
        name: gv.name,
        inputs: [],
        outputs: [gv.name],
        attributes: {},
        shape: gv.shape,
        dtype: gv.dtype,
        graphValue: gv,
      },
      initialWidth: NODE_W,
      initialHeight: ION_H,
    });

    g.setNode(id, { width: NODE_W, height: ION_H });
    outputToNodeId.set(gv.name, id);
    nodeIdToOpType.set(id, 'Input');
    nodeIdToName.set(id, gv.name);
  }

  for (let i = 0; i < graph.nodes.length; i++) {
    const node = graph.nodes[i];
    // Nodes that are also initializers (e.g. SavedModel VarHandleOp) act as variable
    // declarations - they're surfaced as weight metadata inside their consumers, not as
    // standalone graph nodes. Keep them in `graph.nodes` for callers like
    // attachCheckpointToGraph but skip them from layout/edges here.
    if (graph.initializers.has(node.name)) continue;
    const id = graphNodeId(node, i);
    const labels = opInputLabels(node.opType);
    const weightInputsRaw = node.inputs
      .map((name, slot) => {
        const init = graph.initializers.get(name);
        return init ? { slot, label: labels[slot] ?? `in_${slot}`, name, ...init } : null;
      })
      .filter((w): w is NonNullable<typeof w> => w !== null);

    const hasSubtitle = !!(node.name && !/^op_\d+$/.test(node.name));
    const nWeights = weightInputsRaw.length;
    const visibleRows = nWeights <= WEIGHT_ROW_LIMIT ? nWeights : WEIGHT_ROW_LIMIT + 1; // +1 for "more" row
    const nodeH =
      CARD_BASE + (hasSubtitle ? SUBTITLE_H : 0) + (visibleRows > 0 ? META_MARGIN + visibleRows * ROW_H : 0);

    flowNodes.push({
      id,
      type: 'graphNode',
      position: { x: 0, y: 0 },
      data: {
        opType: node.opType,
        name: node.name,
        inputs: node.inputs,
        outputs: node.outputs,
        attributes: node.attributes,
        graphNode: node,
        weightInputs: weightInputsRaw,
      },
      initialWidth: NODE_W,
      initialHeight: nodeH,
    });

    g.setNode(id, { width: NODE_W, height: nodeH });
    for (const out of node.outputs) outputToNodeId.set(out, id);

    nodeIdToOpType.set(id, node.opType);
    nodeIdToName.set(id, node.name || `${node.opType}_${i}`);
  }

  for (let i = 0; i < graph.outputs.length; i++) {
    const gv = graph.outputs[i];
    const id = uniqueIoId('output', gv.name, i);
    flowNodes.push({
      id,
      type: 'ioNode',
      position: { x: 0, y: 0 },
      data: {
        opType: 'Output',
        name: gv.name,
        inputs: [gv.name],
        outputs: [],
        attributes: {},
        shape: gv.shape,
        dtype: gv.dtype,
        graphValue: gv,
      },
      initialWidth: NODE_W,
      initialHeight: ION_H,
    });

    g.setNode(id, { width: NODE_W, height: ION_H });
    nodeIdToOpType.set(id, 'Output');
    nodeIdToName.set(id, gv.name);
  }

  // Track emitted (source, target, tensor) triples so that ops which consume the
  // same tensor in multiple slots (e.g. CONCAT(x,x,...,x) for tf.repeat) produce
  // one FlowEdge rather than N visually-overlapping ones.
  const emittedEdgeKeys = new Set<string>();

  for (const fn of flowNodes) {
    if (fn.type === 'ioNode' && fn.data.opType === 'Input') continue;

    for (let slot = 0; slot < fn.data.inputs.length; slot++) {
      const inputName = fn.data.inputs[slot];
      const srcId = outputToNodeId.get(inputName);
      if (!srcId) continue;

      const edgeKey = `${srcId}=>${fn.id}::${inputName}`;
      if (emittedEdgeKeys.has(edgeKey)) continue;
      emittedEdgeKeys.add(edgeKey);

      flowEdges.push({
        id: edgeKey,
        source: srcId,
        target: fn.id,
        type: 'modelEdge',
        data: {
          tensorName: inputName,
          sourceOpType: nodeIdToOpType.get(srcId) ?? '',
          sourceNodeName: nodeIdToName.get(srcId) ?? '',
          targetOpType: fn.data.opType,
          targetNodeName: nodeIdToName.get(fn.id) ?? '',
        },
      });

      if (!g.hasEdge(srcId, fn.id)) g.setEdge(srcId, fn.id);
    }
  }

  Dagre.layout(g);

  for (const fn of flowNodes) {
    const pos = g.node(fn.id);
    if (pos) fn.position = { x: pos.x - NODE_W / 2, y: pos.y - pos.height / 2 };
  }

  // Count edges per source->target pair so duplicates can be fanned out symmetrically.
  const pairCount = new Map<string, number>();
  for (const fe of flowEdges) {
    const key = `${fe.source}::${fe.target}`;
    pairCount.set(key, (pairCount.get(key) ?? 0) + 1);
  }

  // Annotate each flow edge with Dagre's intermediate waypoints so the edge
  // component can draw a catmull-rom spline that routes around intermediate nodes.
  // Edges that share a source->target pair reuse the same dagre waypoints but get
  // a small horizontal offset per slot so they fan out instead of stacking.
  const ptCache = new Map<string, readonly { x: number; y: number }[]>();
  const slotIndex = new Map<string, number>();
  const FAN_STEP = 8;
  for (let i = 0; i < flowEdges.length; i++) {
    const fe = flowEdges[i];
    const key = `${fe.source}::${fe.target}`;
    let pts = ptCache.get(key);
    if (pts === undefined) {
      const raw = (g.edge(fe.source, fe.target) as { points?: { x: number; y: number }[] } | undefined)?.points;
      // Slice off first and last - those are dagre's node-center estimates;
      // the edge component uses xyflow's actual handle positions instead.
      pts = raw && raw.length > 2 ? raw.slice(1, raw.length - 1) : [];
      ptCache.set(key, pts);
    }

    const total = pairCount.get(key) ?? 1;
    const slot = slotIndex.get(key) ?? 0;
    slotIndex.set(key, slot + 1);
    // Center the fan around 0: for total=2 -> offsets [-FAN_STEP/2, +FAN_STEP/2];
    // for total=3 -> [-FAN_STEP, 0, +FAN_STEP].
    const offset = total > 1 ? (slot - (total - 1) / 2) * FAN_STEP : 0;

    if (pts.length > 0) {
      const fanned = offset === 0 ? pts : pts.map((p) => ({ x: p.x + offset, y: p.y }));
      flowEdges[i] = { ...fe, data: { ...fe.data, points: fanned } };
    }
  }

  return { nodes: flowNodes, edges: flowEdges };
}
